import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Shield, Trash2, RefreshCw, Filter, CheckSquare, Square, LayoutList, Bot, Users, Activity } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Loading from "../components/Loading";
import DroidAnalytics from "../components/DroidAnalytics";
import AdminUsers from "../components/AdminUsers";
import AdminActivity from "../components/AdminActivity";

const SECTIONS = [
  { key: "all", label: "ALL" },
  { key: "news", label: "NEWS" },
  { key: "rocket", label: "ROCKETS" },
  { key: "launch", label: "LAUNCHES" },
  { key: "event", label: "EVENTS" },
];

function titleFor(item) {
  const d = item.data || {};
  return d.title || d.name || d.full_name || d.headline || "(untitled)";
}

export default function AdminPage() {
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [section, setSection] = useState("all");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [tab, setTab] = useState("content"); // 'content' | 'droid'

  useEffect(() => {
    if (!ready) return;
    if (!user?.is_admin) {
      navigate("/pilot", { replace: true });
    }
  }, [ready, user, navigate]);

  const load = useCallback(async () => {
    if (!user?.is_admin) return;
    setLoading(true);
    try {
      const q = section === "all" ? "" : `?section=${section}`;
      const r = await api.get(`/admin/content${q}`);
      setRows(r.data?.results || []);
      setSelected(new Set());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "FAILED TO LOAD ADMIN CONTENT");
    } finally {
      setLoading(false);
    }
  }, [section, user]);

  useEffect(() => { load(); }, [load]);

  const toggle = (id) => {
    setSelected(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };
  const toggleAll = () => {
    setSelected(prev => prev.size === rows.length ? new Set() : new Set(rows.map(r => r.id)));
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} item(s)? This cannot be undone.`)) return;
    let ok = 0, fail = 0;
    for (const id of selected) {
      try { await api.delete(`/admin/content/${id}`); ok++; }
      catch { fail++; }
    }
    toast[fail ? "warning" : "success"](`DELETED ${ok}${fail ? ` · ${fail} FAILED` : ""}`);
    load();
  };

  if (!ready || !user?.is_admin) return null;

  const counts = SECTIONS.reduce((acc, s) => {
    acc[s.key] = s.key === "all" ? rows.length : rows.filter(r => r.section === s.key).length;
    return acc;
  }, {});

  return (
    <div data-testid="page-admin">
      <div className="mb-6 border-b border-[color:var(--sat-border)] pb-4">
        <div className="sat-label sat-cyan flex items-center gap-2"><Shield size={12} /> DIRECTIVE // ADMIN</div>
        <h1 className="font-display font-bold text-3xl md:text-4xl mt-1">COMMAND DASHBOARD</h1>
        <p className="text-sm text-[color:var(--sat-text-dim)] mt-2">
          Unified control over custom content · R2-A2 usage analytics.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-[color:var(--sat-border)]" data-testid="admin-tabs">
        <button
          data-testid="admin-tab-content"
          onClick={() => setTab("content")}
          className={`px-4 py-2 font-mono text-[11px] tracking-[0.2em] uppercase border-b-2 -mb-px flex items-center gap-2 ${
            tab === "content"
              ? "sat-cyan border-[color:var(--sat-cyan)]"
              : "text-[color:var(--sat-text-dim)] border-transparent hover:sat-cyan"
          }`}
        >
          <LayoutList size={12} /> CONTENT
        </button>
        <button
          data-testid="admin-tab-droid"
          onClick={() => setTab("droid")}
          className={`px-4 py-2 font-mono text-[11px] tracking-[0.2em] uppercase border-b-2 -mb-px flex items-center gap-2 ${
            tab === "droid"
              ? "sat-cyan border-[color:var(--sat-cyan)]"
              : "text-[color:var(--sat-text-dim)] border-transparent hover:sat-cyan"
          }`}
        >
          <Bot size={12} /> R2-A2 ANALYTICS
        </button>
        <button
          data-testid="admin-tab-users"
          onClick={() => setTab("users")}
          className={`px-4 py-2 font-mono text-[11px] tracking-[0.2em] uppercase border-b-2 -mb-px flex items-center gap-2 ${
            tab === "users"
              ? "sat-cyan border-[color:var(--sat-cyan)]"
              : "text-[color:var(--sat-text-dim)] border-transparent hover:sat-cyan"
          }`}
        >
          <Users size={12} /> PILOTS
        </button>
        <button
          data-testid="admin-tab-activity"
          onClick={() => setTab("activity")}
          className={`px-4 py-2 font-mono text-[11px] tracking-[0.2em] uppercase border-b-2 -mb-px flex items-center gap-2 ${
            tab === "activity"
              ? "sat-cyan border-[color:var(--sat-cyan)]"
              : "text-[color:var(--sat-text-dim)] border-transparent hover:sat-cyan"
          }`}
        >
          <Activity size={12} /> ACTIVITY
        </button>
      </div>

      {tab === "droid" && <DroidAnalytics />}
      {tab === "users" && <AdminUsers />}
      {tab === "activity" && <AdminActivity />}

      {tab === "content" && <>
      {/* Filter chips */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="admin-filters">
        {SECTIONS.map(s => (
          <button
            key={s.key}
            data-testid={`admin-filter-${s.key}`}
            onClick={() => setSection(s.key)}
            className={`sat-btn !py-2 !px-3 ${section === s.key ? "sat-btn-solid" : ""}`}
          >
            <Filter size={11} /> {s.label} · {counts[s.key] ?? 0}
          </button>
        ))}
        <button onClick={load} className="sat-btn !py-2 !px-3 ml-auto" data-testid="admin-refresh">
          <RefreshCw size={11} /> REFRESH
        </button>
      </div>

      {/* Bulk actions */}
      {rows.length > 0 && (
        <div className="flex items-center gap-2 mb-3" data-testid="admin-bulk-bar">
          <button onClick={toggleAll} className="sat-btn !py-2 !px-3" data-testid="admin-select-all">
            {selected.size === rows.length && rows.length > 0
              ? <CheckSquare size={11} />
              : <Square size={11} />}
            SELECT ALL
          </button>
          <div className="sat-label text-[color:var(--sat-text-dim)]">{selected.size} SELECTED</div>
          <button
            onClick={bulkDelete}
            disabled={selected.size === 0}
            data-testid="admin-bulk-delete"
            className="sat-btn sat-btn-danger !py-2 !px-3 disabled:opacity-40 ml-auto"
          >
            <Trash2 size={11} /> BULK DELETE
          </button>
        </div>
      )}

      {loading && <Loading message="LOADING CONTENT" />}

      {!loading && rows.length === 0 && (
        <div className="sat-label text-center py-12" data-testid="admin-empty">NO CUSTOM CONTENT IN THIS SECTION</div>
      )}

      {!loading && rows.length > 0 && (
        <div className="sat-surface border border-[color:var(--sat-border)] overflow-x-auto" data-testid="admin-table">
          <table className="w-full min-w-[720px] text-sm">
            <thead>
              <tr className="border-b border-[color:var(--sat-border)] bg-black/50">
                <th className="text-left sat-label px-3 py-2 w-8"> </th>
                <th className="text-left sat-label px-3 py-2 w-24">SECTION</th>
                <th className="text-left sat-label px-3 py-2">TITLE</th>
                <th className="text-left sat-label px-3 py-2 w-40">CREATED</th>
                <th className="text-left sat-label px-3 py-2 w-52">BY</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const isSel = selected.has(r.id);
                return (
                  <tr key={r.id} className="border-b border-[color:var(--sat-border)] hover:bg-black/40" data-testid={`admin-row-${r.id}`}>
                    <td className="px-3 py-2">
                      <button
                        onClick={() => toggle(r.id)}
                        data-testid={`admin-select-${r.id}`}
                        aria-label={isSel ? "Deselect" : "Select"}
                        className="text-[color:var(--sat-cyan)] hover:opacity-80"
                      >
                        {isSel ? <CheckSquare size={14} /> : <Square size={14} />}
                      </button>
                    </td>
                    <td className="px-3 py-2 sat-label sat-cyan">{(r.section || "").toUpperCase()}</td>
                    <td className="px-3 py-2 font-display font-bold">{titleFor(r)}</td>
                    <td className="px-3 py-2 font-mono text-[11px] text-[color:var(--sat-text-dim)]">
                      {r.created_at ? new Date(r.created_at).toISOString().slice(0, 16).replace("T", " ") : "—"}
                    </td>
                    <td className="px-3 py-2 font-mono text-[11px] text-[color:var(--sat-text-dim)]">
                      {r.created_by || "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="sat-label text-[color:var(--sat-text-dim)] mt-4">
        NOTE: TO EDIT CONTENT, USE INLINE ADMIN CONTROLS ON EACH LIST PAGE (
        <Link to="/news" className="sat-cyan">/news</Link>{" · "}
        <Link to="/fleet" className="sat-cyan">/fleet</Link>{" · "}
        <Link to="/launches" className="sat-cyan">/launches</Link>{" · "}
        <Link to="/events" className="sat-cyan">/events</Link>
        ).
      </p>
      </>}
    </div>
  );
}
